// OEM CONFIRMATIONS on the quote screen -- Order Management O1/O2 (owner
// decisions D181, 2026-10-30).
//
// Per line: what the OEM has CONFIRMED (price, quantity, lead time, validity,
// reference, who recorded it) against the cost the QUOTE used.
//
// ⚠️ THE PANEL IS NOT THE GUARD. The backend decides who may see (Sales: own
// quotes only; Viewer: read-only; Admin: any) and who may record, and refuses
// a missing price or quantity (test_oem_confirmation.py). This renders what
// it was told and offers only what the role may do.
//
// ⚠️ NOTHING HERE PRICES. A different confirmed price is SHOWN, in amber,
// never applied: the quote's figures do not move. Acting on it is a Revise.
//
// ⚠️ ABSENT IS NOT ZERO. A lead time or validity the OEM did not state reads
// "Not stated"; the price and quantity inputs start EMPTY, never at 0.

import { useCallback, useEffect, useState } from "react";
import {
  getOemConfirmations, recordOemConfirmation, withdrawOemConfirmation, acceptOemDifference,
  type OemConfirmation, type OemConfirmationLine, type OemConfirmationsView,
  type OemBadge, type OemLineStatus, type OemQuoteStatus,
} from "@/services/api";
import { formatDate, formatRupees, moneyLocale } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const CURRENCIES = ["INR", "USD", "EUR", "CNY"] as const;

export const QUOTE_STATUS_LABEL: Record<OemQuoteStatus, string> = {
  draft: "Draft",
  awaiting_oem: "Awaiting OEM confirmation",
  oem_confirmed: "OEM confirmed",
  sent: "Sent",
  // The quote's own pill says Won; the same word everywhere (ux-guided-deal).
  // The server's value stays "accepted".
  accepted: "Won",
  lost: "Lost",
  expired: "Expired",
  superseded: "Superseded",
};

export const BADGE_LABEL: Record<OemBadge, string> = {
  price_differs: "OEM price differs",
  currency_differs: "OEM currency differs",
  oem_expired: "OEM confirmation expired",
  oem_validity_before_quote: "OEM validity ends before the quote's",
  awaiting_oem: "Awaiting OEM confirmation",
};

const LINE_STATUS: Record<OemLineStatus, { label: string; tone: "green" | "amber" | "red" | "gray" }> = {
  confirmed: { label: "Confirmed", tone: "green" },
  partial: { label: "Partly confirmed", tone: "amber" },
  unconfirmed: { label: "Not confirmed", tone: "gray" },
  lead_time_not_stated: { label: "Lead time not stated", tone: "amber" },
  validity_not_stated: { label: "Validity not stated", tone: "amber" },
  expired: { label: "Expired", tone: "red" },
  not_quoted: { label: "Not quoted", tone: "gray" },
};

const TONE = {
  green: { fg: colors.green600, bg: colors.green50, border: colors.green100 },
  amber: { fg: colors.amber800, bg: colors.amber50, border: colors.amber200 },
  red: { fg: colors.red600, bg: colors.red50, border: colors.red100 },
  gray: { fg: semanticColors.textSecondary, bg: semanticColors.panelBg, border: semanticColors.border },
} as const;

export function Pill({ tone, children, testId }: { tone: keyof typeof TONE; children: React.ReactNode; testId?: string }) {
  const t = TONE[tone];
  return (
    <span data-testid={testId} style={{
      display: "inline-flex", alignItems: "center", height: heights.pill, padding: `0 ${spacing[8]}px`,
      borderRadius: radius.base, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
      color: t.fg, backgroundColor: t.bg, border: `1px solid ${t.border}`, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

export function quoteStatusTone(s: OemQuoteStatus): keyof typeof TONE {
  if (s === "oem_confirmed" || s === "accepted") return "green";
  if (s === "awaiting_oem") return "amber";
  if (s === "lost" || s === "expired") return "red";
  return "gray";
}

/** Money in its own currency. INR follows the app's configured format. */
export function money(value: number, currency: string): string {
  if (currency === "INR") return formatRupees(value);
  return `${currency} ${value.toLocaleString(moneyLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function leadTimeLabel(c: Pick<OemConfirmation, "lead_time">): string {
  const lt = c.lead_time;
  if (lt.kind === "days" && lt.days !== null) return `${lt.days} day${lt.days === 1 ? "" : "s"}`;
  if (lt.kind === "date" && lt.date) return formatDate(lt.date);
  return "Not stated";
}

/** The finalise dialog's warning, or null. WARNS, never blocks (D181 #2). */
export function oemFinaliseWarning(view: OemConfirmationsView | null): string | null {
  if (!view || view.priced_lines === 0 || view.awaiting_lines === 0) return null;
  return `${view.awaiting_lines} of ${view.priced_lines} quoted line${view.priced_lines === 1 ? "" : "s"} ` +
    "have no valid OEM confirmation. You can finalise; an OEM order will need them confirmed.";
}

const BOX: React.CSSProperties = {
  marginTop: spacing[12], border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface,
};
const TH: React.CSSProperties = {
  textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.xs,
  fontWeight: typography.fontWeight.medium, color: semanticColors.textMuted, whiteSpace: "nowrap",
  borderBottom: `1px solid ${semanticColors.border}`,
};
const TD: React.CSSProperties = {
  padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.sm,
  color: semanticColors.textPrimary, verticalAlign: "top", borderBottom: `1px solid ${semanticColors.border}`,
};
const BTN: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[10]}px`, backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.sm, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = { ...BTN, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none" };
const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, minWidth: 0,
};

function DiffCell({ line, costCurrency }: { line: OemConfirmationLine; costCurrency: string }) {
  if (!line.differences.length) return <span style={{ color: semanticColors.textMuted }}>—</span>;
  return (
    <div style={{ display: "grid", gap: spacing[2] }}>
      {line.differences.map((d) => {
        if (!d.comparable) {
          return <span key={d.confirmation_id} data-testid={`oemc-diff-${line.line_no}`}
                       style={{ color: colors.amber800 }}>Currency differs — not compared</span>;
        }
        const per = d.per_unit ?? 0;
        if (Math.abs(per) < 1e-9) {
          return <span key={d.confirmation_id} data-testid={`oemc-diff-${line.line_no}`}
                       style={{ color: semanticColors.textSecondary }}>Same as the quote</span>;
        }
        const sign = per > 0 ? "+" : "−";
        return (
          <span key={d.confirmation_id} data-testid={`oemc-diff-${line.line_no}`} style={{ color: colors.amber800 }}>
            {sign}{money(Math.abs(per), costCurrency)} per unit ({sign}{money(Math.abs(d.on_confirmed_qty ?? 0), costCurrency)} on the confirmed qty)
          </span>
        );
      })}
    </div>
  );
}

function RecordForm({
  line, costCurrency, active, onDone, onCancel,
}: {
  line: OemConfirmationLine; costCurrency: string; active: OemConfirmation[];
  onDone: () => void; onCancel: () => void;
}) {
  const remaining = Math.max(0, line.qty - active.reduce((s, c) => s + c.confirmed_qty, 0));
  // EMPTY, never 0: an untouched price is "not entered", which the server refuses.
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState<string>(CURRENCIES.includes(costCurrency as never) ? costCurrency : "INR");
  const [qty, setQty] = useState(remaining > 0 ? String(remaining) : "");
  const [kind, setKind] = useState<"days" | "date" | "not_stated">("days");
  const [days, setDays] = useState("");
  const [ltDate, setLtDate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [ref, setRef] = useState("");
  const [supersedes, setSupersedes] = useState<number | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (price.trim() === "") { setError("Enter the price the OEM confirmed."); return; }
    if (qty.trim() === "") { setError("Enter the quantity the confirmation covers."); return; }
    if (kind === "days" && days.trim() === "") { setError("Enter the lead time in days, or choose Not stated."); return; }
    if (kind === "date" && !ltDate) { setError("Enter the lead time date, or choose Not stated."); return; }
    setBusy(true);
    const res = await recordOemConfirmation({
      line_id: line.line_id,
      confirmed_unit_price: Number(price),
      currency,
      confirmed_qty: Number(qty),
      lead_time: kind === "days" ? { kind, days: Number(days) } : kind === "date" ? { kind, date: ltDate } : { kind },
      valid_until: validUntil || null,
      oem_reference: ref.trim() || undefined,
      ...(supersedes !== "" ? { supersedes_id: supersedes } : {}),
    });
    setBusy(false);
    if (!res.ok) { setError(res.error || "The confirmation was not recorded."); return; }
    onDone();
  };

  const field = (label: string, el: React.ReactNode) => (
    <label style={{ display: "grid", gap: spacing[2], fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
      {label}{el}
    </label>
  );

  return (
    <div data-testid={`oemc-form-${line.line_no}`} style={{
      padding: spacing[12], backgroundColor: semanticColors.panelBg, borderBottom: `1px solid ${semanticColors.border}`,
      display: "grid", gap: spacing[10],
    }}>
      <div style={{ display: "grid", gap: spacing[10], gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
        {field(`Confirmed unit price`, <input data-testid="oemc-price" style={INPUT} inputMode="decimal"
          value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 7128.00" />)}
        {field("Currency", <select data-testid="oemc-currency" style={INPUT} value={currency}
          onChange={(e) => setCurrency(e.target.value)}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select>)}
        {field(`Quantity covered (line ${line.qty})`, <input data-testid="oemc-qty" style={INPUT} inputMode="decimal"
          value={qty} onChange={(e) => setQty(e.target.value)} />)}
        {field("Lead time", (
          <div style={{ display: "flex", gap: spacing[6] }}>
            <select data-testid="oemc-lt-kind" style={INPUT} value={kind}
              onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="days">Days</option><option value="date">Date</option><option value="not_stated">Not stated</option>
            </select>
            {kind === "days" && <input data-testid="oemc-lt-days" style={{ ...INPUT, flex: 1 }} inputMode="numeric"
              value={days} onChange={(e) => setDays(e.target.value)} placeholder="days" />}
            {kind === "date" && <input data-testid="oemc-lt-date" type="date" style={INPUT}
              value={ltDate} onChange={(e) => setLtDate(e.target.value)} />}
          </div>
        ))}
        {field("Valid until", <input data-testid="oemc-valid" type="date" style={INPUT}
          value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />)}
        {field("OEM reference", <input data-testid="oemc-ref" style={INPUT} value={ref}
          onChange={(e) => setRef(e.target.value)} placeholder="their quote / ack no." />)}
        {active.length > 0 && field("Replaces", (
          <select data-testid="oemc-supersedes" style={INPUT} value={supersedes}
            onChange={(e) => setSupersedes(e.target.value === "" ? "" : Number(e.target.value))}>
            <option value="">Nothing — adds to what is confirmed</option>
            {active.map((c) => <option key={c.id} value={c.id}>#{c.id}: {c.confirmed_qty} @ {money(c.confirmed_unit_price, c.currency)}</option>)}
          </select>
        ))}
      </div>
      {error && <div data-testid="oemc-error" role="alert" style={{ color: colors.red600, fontSize: typography.fontSize.sm }}>{error}</div>}
      <div style={{ display: "flex", gap: spacing[8], justifyContent: "flex-end" }}>
        <button type="button" style={BTN} onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" data-testid="oemc-save" style={PRIMARY} onClick={() => { void submit(); }} disabled={busy}>
          {busy ? "Recording…" : "Record confirmation"}
        </button>
      </div>
    </div>
  );
}

export function OemConfirmationsPanel({
  quoteId, reloadKey = 0, onView, openRecord = 0,
}: {
  quoteId: number;
  reloadKey?: number;
  /** The loaded view, for the finalise dialog's warning. */
  onView?: (view: OemConfirmationsView | null) => void;
  /** ux-guided-deal: bumped by the next-step card to open the Record form on
   *  the first quoted line that is not confirmed yet. */
  openRecord?: number;
}) {
  const [view, setView] = useState<OemConfirmationsView | null>(null);
  const [error, setError] = useState("");
  const [hidden, setHidden] = useState(false);
  const [openForm, setOpenForm] = useState<number | null>(null);
  const [history, setHistory] = useState<Record<number, boolean>>({});
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    const res = await getOemConfirmations(quoteId);
    if (!res.ok) {
      // "not found" = this caller may not see this quote's confirmations
      // (a Sales user on another's quote): the panel is simply absent.
      if (res.notFound) { setHidden(true); setView(null); onView?.(null); return; }
      setError(res.error || "The OEM confirmations could not be read."); setView(null); onView?.(null); return;
    }
    setHidden(false); setError(""); setView(res.view!); onView?.(res.view!);
  }, [quoteId, onView]);

  useEffect(() => { void load(); }, [load, reloadKey, tick]);
  useEffect(() => {
    if (openRecord <= 0 || !view?.can_record) return;
    const line = view.lines.find((l) => l.status !== "not_quoted" && l.status !== "confirmed") ?? null;
    if (line) setOpenForm(line.line_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRecord]);

  if (hidden) return null;

  const accept = async (c: OemConfirmation) => {
    // D181 #5: Admin only, a written reason, the margin impact shown first.
    const reason = window.prompt(`${c.margin_impact ?? ""}\n\nWhy is this difference accepted? (required)`);
    if (reason === null) return;
    const res = await acceptOemDifference(c.id, reason);
    if (!res.ok) { setError(res.error || "The difference was not accepted."); return; }
    setTick((t) => t + 1);
  };

  const withdraw = async (c: OemConfirmation) => {
    const reason = window.prompt("Why is this confirmation withdrawn?");
    if (reason === null) return;
    const res = await withdrawOemConfirmation(c.id, reason);
    if (!res.ok) { setError(res.error || "The confirmation was not withdrawn."); return; }
    setTick((t) => t + 1);
  };

  return (
    <section data-testid="oem-confirmations" style={BOX}>
      <div style={{
        display: "flex", alignItems: "center", gap: spacing[10], flexWrap: "wrap",
        padding: `${spacing[10]}px ${spacing[12]}px`, borderBottom: `1px solid ${semanticColors.border}`,
      }}>
        <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary }}>
          OEM confirmations
        </span>
        {view && <Pill testId="oemc-quote-status" tone={quoteStatusTone(view.status)}>{QUOTE_STATUS_LABEL[view.status]}</Pill>}
        {view && (
          <span data-testid="oemc-count" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
            {view.confirmed_lines} of {view.priced_lines} quoted line{view.priced_lines === 1 ? "" : "s"} confirmed
          </span>
        )}
        {view?.badges.map((b) => (
          <Pill key={b} testId={`oemc-badge-${b}`} tone={b === "oem_expired" ? "red" : "amber"}>{BADGE_LABEL[b]}</Pill>
        ))}
      </div>
      <div style={{ padding: `${spacing[6]}px ${spacing[12]}px`, fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
        When the OEM replies, press Record on the line and type what it confirmed.
        What each OEM has confirmed, against the cost this quote used ({view?.cost_currency ?? "—"}). A different
        price is shown here and never applied: to use it, revise the quote.
      </div>
      {error && <div data-testid="oemc-load-error" role="alert" style={{ padding: spacing[12], color: colors.red600, fontSize: typography.fontSize.sm }}>{error}</div>}
      {view && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["#", "Part", "OEM", "Qty", "Quote cost", "Status", "Confirmed", "Difference", "Lead time", "Valid until", "Recorded", ""].map((h) => (
                  <th key={h} style={TH}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {view.lines.map((line) => {
                const active = line.confirmations.filter((c) => c.state === "active");
                const latest = active[0];
                const st = LINE_STATUS[line.status];
                const rows = [
                  <tr key={line.line_id} data-testid={`oemc-line-${line.line_no}`}>
                    <td style={TD}>{line.line_no}</td>
                    <td style={{ ...TD, fontWeight: typography.fontWeight.medium, whiteSpace: "nowrap" }}>{line.part_number}</td>
                    <td style={TD}>{line.oem || "—"}</td>
                    <td style={TD}>{line.qty}</td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }}>{line.status === "not_quoted" ? "—" : money(line.unit_price, view.cost_currency)}</td>
                    <td style={TD}><Pill testId={`oemc-status-${line.line_no}`} tone={st.tone}>{st.label}</Pill></td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }} data-testid={`oemc-confirmed-${line.line_no}`}>
                      {latest ? `${line.confirmed_qty} @ ${money(latest.confirmed_unit_price, latest.currency)}` : "—"}
                    </td>
                    <td style={TD}><DiffCell line={line} costCurrency={view.cost_currency} /></td>
                    <td style={TD} data-testid={`oemc-lead-${line.line_no}`}>{latest ? leadTimeLabel(latest) : "—"}</td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }} data-testid={`oemc-valid-${line.line_no}`}>
                      {latest ? (latest.valid_until ? formatDate(latest.valid_until) : "Not stated") : "—"}
                    </td>
                    <td style={{ ...TD, color: semanticColors.textSecondary }}>
                      {latest ? `${latest.recorded_by ?? "Unknown"}${latest.oem_reference ? ` · ${latest.oem_reference}` : ""}` : "—"}
                    </td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }}>
                      {view.can_record && line.status !== "not_quoted" && (
                        <button type="button" data-testid={`oemc-record-${line.line_no}`} style={BTN}
                          onClick={() => setOpenForm(openForm === line.line_id ? null : line.line_id)}>Record</button>
                      )}{" "}
                      {line.confirmations.length > 0 && (
                        <button type="button" data-testid={`oemc-history-${line.line_no}`} style={BTN}
                          onClick={() => setHistory((h) => ({ ...h, [line.line_id]: !h[line.line_id] }))}>
                          History ({line.confirmations.length})
                        </button>
                      )}
                    </td>
                  </tr>,
                ];
                if (openForm === line.line_id) {
                  rows.push(
                    <tr key={`f${line.line_id}`}><td colSpan={12} style={{ padding: 0 }}>
                      <RecordForm line={line} costCurrency={view.cost_currency} active={active}
                        onCancel={() => setOpenForm(null)}
                        onDone={() => { setOpenForm(null); setTick((t) => t + 1); }} />
                    </td></tr>,
                  );
                }
                if (history[line.line_id]) {
                  rows.push(
                    <tr key={`h${line.line_id}`}><td colSpan={12} style={{ ...TD, backgroundColor: semanticColors.panelBg }}>
                      <div data-testid={`oemc-history-list-${line.line_no}`} style={{ display: "grid", gap: spacing[4] }}>
                        {line.confirmations.map((c) => (
                          <div key={c.id} style={{ display: "flex", gap: spacing[8], alignItems: "center", flexWrap: "wrap" }}>
                            <Pill tone={c.state === "active" ? "green" : "gray"}>{c.state}</Pill>
                            <span>#{c.id}: {c.confirmed_qty} @ {money(c.confirmed_unit_price, c.currency)}, lead time {leadTimeLabel(c)}, valid until {c.valid_until ? formatDate(c.valid_until) : "not stated"}</span>
                            <span style={{ color: semanticColors.textMuted }}>
                              recorded by {c.recorded_by ?? "unknown"}{c.withdraw_reason ? ` · withdrawn: ${c.withdraw_reason}` : ""}
                            </span>
                            {c.diff_accepted_at && (
                              <span data-testid={`oemc-accepted-${c.id}`} style={{ color: colors.amber800 }}>
                                difference accepted by {c.diff_accepted_by ?? "—"}: {c.diff_accept_reason} ({c.diff_accept_impact})
                              </span>
                            )}
                            {(c.ordered_qty ?? 0) > 0 && (
                              <span style={{ color: semanticColors.textSecondary }}>ordered {c.ordered_qty}</span>
                            )}
                            {view.can_record && c.state === "active" && (
                              <button type="button" data-testid={`oemc-withdraw-${c.id}`} style={BTN}
                                onClick={() => { void withdraw(c); }}>Withdraw</button>
                            )}
                            {view.can_accept_difference && c.state === "active" && !c.diff_accepted_at
                              && line.differences.some((d) => d.confirmation_id === c.id
                                && (!d.comparable || Math.abs(d.per_unit ?? 0) > 1e-9)) && (
                              <button type="button" data-testid={`oemc-accept-${c.id}`} style={BTN}
                                title={c.margin_impact ?? undefined}
                                onClick={() => { void accept(c); }}>Accept difference</button>
                            )}
                          </div>
                        ))}
                      </div>
                    </td></tr>,
                  );
                }
                return rows;
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
